import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  Eye,
  EyeOff,
  FileCode,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  Paperclip,
  PenSquare,
  Plus,
  Reply,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal, SlideOver } from '../components';
import {
  fetchOutreachTemplates,
  fetchOutreachThreads,
  fetchOutreachThread,
  updateOutreachThread,
  previewOutreachEmail,
  sendOutreachEmail,
  replyToOutreachThread,
  allowOutreachEmailsAgain,
} from '../services/outreachService';

/**
 * Mailbox — the admin's outreach inbox for sameer@servpatch.co.uk.
 *
 * servpatch.co.uk has no other mailbox: everything sent to it arrives here
 * (Cloudflare → Brevo → the API), and everything sent from it goes out from
 * here. Left: conversations by tab. Right: the open conversation and a reply
 * box. "New email" opens the composer with a live preview of the real email.
 *
 * Received HTML is untrusted, so it is only ever shown inside a sandboxed
 * iframe with scripts off; the conversation itself shows the plain text.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// There is no push channel for email, so the list refreshes on a timer.
const POLL_MS = 30000;

const TABS = [
  { key: 'open', label: 'Inbox', icon: Inbox },
  { key: 'archived', label: 'Archived', icon: Archive },
  { key: 'spam', label: 'Spam', icon: ShieldAlert },
];

const PILL = {
  gray: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-[#31373f] dark:text-gray-300 dark:border-gray-700',
  blue: 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/50',
  emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-900/50',
  amber: 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-900/50',
  red: 'bg-red-50 text-red-600 border-red-100 dark:bg-red-900/10 dark:text-red-400 dark:border-red-900/50',
};

const DELIVERY = {
  sent: { label: 'Sent', tone: 'gray' },
  delivered: { label: 'Delivered', tone: 'blue' },
  opened: { label: 'Opened', tone: 'emerald' },
  clicked: { label: 'Clicked', tone: 'emerald' },
  soft_bounce: { label: 'Delayed', tone: 'amber' },
  bounced: { label: 'Bounced', tone: 'red' },
  blocked: { label: 'Blocked', tone: 'red' },
  spam: { label: 'Marked as spam', tone: 'red' },
  failed: { label: 'Failed', tone: 'red' },
};

const OPT_OUT_REASONS = {
  unsubscribe_link: 'used the unsubscribe link',
  one_click: 'unsubscribed from their mail app',
  replied_no_thanks: 'replied “no thanks”',
  hard_bounce: 'the address bounced',
  spam_complaint: 'marked an email as spam',
};

const SPAM_SCORE_WARNING = 5;

const inputCls = 'w-full px-3.5 py-2.5 bg-white dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 focus:border-[#2563EB] text-[14px] text-[#1E293B] dark:text-gray-200 transition-all';
const ghostBtn = 'inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 bg-white dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 text-[#475569] dark:text-gray-300 text-[12.5px] font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-[#3a414b] transition-all disabled:opacity-50';
const primaryBtn = 'inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 bg-[#2563EB] text-white text-[13px] font-bold rounded-lg hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed';

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const fileHref = (url) => (/^https?:\/\//i.test(url) ? url : `${API_ORIGIN}${url}`);

const listStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
};

const fullStamp = (iso) => (iso
  ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
  : '');

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const initialOf = (name, email) => (String(name || email || '?').trim().charAt(0) || '?').toUpperCase();

/**
 * An email's text with its links made clickable — built as React elements,
 * never as HTML, so nothing in a received email can inject markup. Brevo hands
 * over the received text as light markdown; links become anchors, emphasis
 * markers are dropped, and inline-image references become their file name.
 */
const RichText = ({ text, className = '' }) => {
  const clean = String(text || '')
    .replace(/!?\[([^\]\n]*)\]\((?!https?:|mailto:)[^)\s]*\)/g, '[$1]')
    .replace(/\*\*|__/g, '')
    .replace(/ {2,}\n/g, '\n');
  const token = /\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^)\s]+)\)|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;
  const parts = [];
  let last = 0;
  let match;
  while ((match = token.exec(clean))) {
    if (match.index > last) parts.push(clean.slice(last, match.index));
    const href = match[2] || match[3];
    parts.push(
      <a key={match.index} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline break-all">
        {match[1] || match[3]}
      </a>,
    );
    last = match.index + match[0].length;
  }
  if (last < clean.length) parts.push(clean.slice(last));
  return <div className={`whitespace-pre-wrap break-words ${className}`}>{parts}</div>;
};

/**
 * The email exactly as it was written, in a sandbox: no scripts, no access
 * to the dashboard, links open in a new tab. Inline images (cid:) point at our
 * stored copies of the attachments.
 */
const EmailFrame = ({ html, attachments = [], height = 520 }) => {
  const srcDoc = useMemo(() => {
    let doc = String(html || '');
    attachments.forEach((a) => {
      if (a.contentId && a.url) doc = doc.split(`cid:${a.contentId}`).join(fileHref(a.url));
    });
    const base = '<base target="_blank">';
    return /<head[^>]*>/i.test(doc) ? doc.replace(/<head[^>]*>/i, (head) => `${head}${base}`) : `${base}${doc}`;
  }, [html, attachments]);

  return (
    <iframe
      title="Email"
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      className="w-full rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white"
      style={{ height }}
    />
  );
};

const DeliveryPill = ({ status }) => {
  const info = DELIVERY[status];
  if (!info) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${PILL[info.tone]}`}>
      {info.label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* One email in a conversation                                         */
/* ------------------------------------------------------------------ */

const CLAMP_AT = 700;

const MessageCard = ({ message, templateLabels }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const out = message.direction === 'out';
  const text = message.body_text || '';
  const long = text.length > CLAMP_AT;
  const shown = long && !expanded ? `${text.slice(0, CLAMP_AT).trimEnd()}…` : text;
  const kindLabel = out
    ? (message.kind === 'reply' ? 'Reply' : templateLabels[message.kind] || 'Email')
    : null;
  const attachments = message.attachments || [];
  const failed = out && ['bounced', 'blocked', 'spam', 'failed', 'soft_bounce'].includes(message.delivery_status);

  return (
    <div
      className={`rounded-2xl border p-4 ${out
        ? 'bg-blue-50/60 border-blue-100 dark:bg-blue-500/5 dark:border-blue-900/40 ml-4 md:ml-14'
        : 'bg-white border-[#E2E8F0] dark:bg-[#262b33] dark:border-gray-800 mr-4 md:mr-14'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-[14px] font-bold ${out
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-600 dark:bg-[#31373f] dark:text-gray-300'}`}
        >
          {initialOf(message.from_name, message.from_email)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[14px] font-bold text-[#1E293B] dark:text-gray-100">
              {message.from_name || message.from_email}
            </span>
            <span className="text-[12px] text-[#94A3B8] truncate">&lt;{message.from_email}&gt;</span>
            {out && <DeliveryPill status={message.delivery_status} />}
          </div>
          <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-2">
            <span>{fullStamp(message.sent_at || message.created_at)}</span>
            {message.to_email && <span>to {message.to_email}</span>}
            {kindLabel && <span>· {kindLabel}{message.sent_by_name ? ` · sent by ${message.sent_by_name}` : ''}</span>}
          </div>
        </div>
      </div>

      {!out && Number(message.spam_score) >= SPAM_SCORE_WARNING && (
        <div className="mt-3 flex items-center gap-2 text-[12px] font-semibold text-amber-700 dark:text-amber-400">
          <AlertTriangle className="w-3.5 h-3.5" /> Looks like spam (score {Number(message.spam_score).toFixed(1)}) — be careful with links.
        </div>
      )}

      <div className="mt-3 text-[14px] leading-relaxed text-[#334155] dark:text-gray-200">
        {text
          ? <RichText text={shown} />
          : <span className="italic text-[#94A3B8]">No plain text — open the original email below.</span>}
        {long && (
          <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1 text-[12.5px] font-semibold text-blue-600 dark:text-blue-400 hover:underline">
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {attachments.map((a, i) => (a.url ? (
            <a
              key={`${a.name}-${i}`}
              href={fileHref(a.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] text-[12.5px] font-medium text-[#334155] dark:text-gray-200 hover:border-blue-300"
            >
              <Paperclip className="w-3.5 h-3.5 text-[#94A3B8]" />
              <span className="max-w-[220px] truncate">{a.name}</span>
              {a.size ? <span className="text-[#94A3B8]">{formatSize(a.size)}</span> : null}
            </a>
          ) : (
            <span
              key={`${a.name}-${i}`}
              title="Still copying from Brevo, or the copy failed"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-[#E2E8F0] dark:border-gray-700 text-[12.5px] text-[#94A3B8]"
            >
              <Paperclip className="w-3.5 h-3.5" />
              <span className="max-w-[220px] truncate">{a.name}</span> · unavailable
            </span>
          )))}
        </div>
      )}

      {failed && message.delivery_error && (
        <div className="mt-3 text-[12.5px] text-red-600 dark:text-red-400">{message.delivery_error}</div>
      )}

      {message.body_html && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#64748B] dark:text-gray-400 hover:text-blue-600"
          >
            {showOriginal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showOriginal ? 'Hide email' : out ? 'View email as sent' : 'View original email'}
          </button>
          {showOriginal && (
            <div className="mt-2">
              <EmailFrame html={message.body_html} attachments={attachments} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Compose a new email                                                 */
/* ------------------------------------------------------------------ */

// Custom HTML limits — the API enforces the hard one; Gmail clips past ~102 KB.
const MAX_HTML_BYTES = 2 * 1024 * 1024;
const GMAIL_CLIP_BYTES = 100 * 1024;

const Warning = ({ children }) => (
  <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 text-[12.5px] text-amber-800 dark:text-amber-300">
    <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
    <span>{children}</span>
  </div>
);

// One send reaches at most this many people — mirrors MAX_RECIPIENTS in
// api/utils/outreachService.js.
const MAX_RECIPIENTS = 50;

let rowSeq = 0;
const newRow = (email = '', name = '') => {
  rowSeq += 1;
  return { id: rowSeq, email, name, error: '' };
};

const EMAIL_IN_TEXT = /[^\s<>,;:"'()[\]]+@[^\s<>,;:"'()[\]]+\.[^\s<>,;:"'()[\]]+/g;

/**
 * Addresses pasted from anywhere: a typed list ("a@x.com, b@y.com"), a mail
 * client ("Jane Carter <jane@x.com>") or two columns copied from Excel. A line
 * holding exactly one address takes the rest of the line as the name.
 */
const parseRecipientList = (text) => {
  const out = [];
  String(text || '').split(/\r?\n/).forEach((line) => {
    const emails = line.match(EMAIL_IN_TEXT) || [];
    if (emails.length === 1) {
      const name = line.replace(emails[0], ' ').replace(/[<>"'\t,;:|]+/g, ' ').replace(/\s+/g, ' ').trim();
      out.push({ email: emails[0], name });
    } else {
      emails.forEach((email) => out.push({ email, name: '' }));
    }
  });
  return out;
};

const emailKey = (row) => row.email.trim().toLowerCase();

/** What is wrong with one row before sending, or '' — a server reason wins. */
const rowIssue = (row, duplicates) => {
  if (row.error) return row.error;
  if (!row.email.trim()) return '';
  if (!isEmail(row.email)) return 'Not a valid email address.';
  if (duplicates.has(emailKey(row))) return 'This address is already in the list.';
  return '';
};

/**
 * The people an email goes to — one row each, email plus an optional name.
 * Each person is sent their own copy, so the name personalises theirs.
 */
const RecipientsEditor = ({ rows, setRows, custom }) => {
  const [focusId, setFocusId] = useState(null);

  const duplicates = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      if (row.email.trim()) counts.set(emailKey(row), (counts.get(emailKey(row)) || 0) + 1);
    });
    return new Set([...counts].filter(([, n]) => n > 1).map(([key]) => key));
  }, [rows]);

  const update = (id, changes) => setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...changes, error: '' } : row)));
  const remove = (id) => setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== id) : [newRow()]));
  const add = () => {
    if (rows.length >= MAX_RECIPIENTS) return;
    const row = newRow();
    setRows((prev) => [...prev, row]);
    setFocusId(row.id);
  };

  // A list pasted into an email box spreads over rows, replacing the row it
  // was pasted into and skipping addresses already listed. A single address
  // pastes normally.
  const pasteInto = (id) => (e) => {
    const text = e.clipboardData.getData('text');
    const parsed = parseRecipientList(text);
    if (parsed.length < 2 && !(parsed.length === 1 && /\t|</.test(text))) return;
    e.preventDefault();
    setRows((prev) => {
      const known = new Set(prev.filter((row) => row.id !== id && row.email.trim()).map(emailKey));
      const fresh = parsed
        .filter((p) => {
          const key = p.email.toLowerCase();
          if (known.has(key)) return false;
          known.add(key);
          return true;
        })
        .map((p) => newRow(p.email, p.name));
      const next = [...prev];
      next.splice(next.findIndex((row) => row.id === id), 1, ...fresh);
      return next.length ? next.slice(0, MAX_RECIPIENTS) : [newRow()];
    });
    if (parsed.length > MAX_RECIPIENTS) toast.error(`Only the first ${MAX_RECIPIENTS} people were added.`);
  };

  const filled = rows.filter((row) => row.email.trim()).length;
  const rowInputCls = 'min-w-0 px-3.5 py-2.5 bg-white dark:bg-[#31373f] border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 focus:border-[#2563EB] text-[14px] text-[#1E293B] dark:text-gray-200 transition-all';

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12.5px] font-bold text-[#475569] dark:text-gray-300">To</span>
        {filled > 1 && <span className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400">{filled} people</span>}
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => {
          const issue = rowIssue(row, duplicates);
          return (
            <div key={row.id}>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={row.email}
                  onChange={(e) => update(row.id, { email: e.target.value })}
                  onPaste={pasteInto(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      add();
                    }
                  }}
                  placeholder="name@carehome.co.uk"
                  autoFocus={row.id === focusId || (index === 0 && rows.length === 1)}
                  className={`${rowInputCls} flex-[1.4] ${issue ? 'border-red-300 dark:border-red-800' : 'border-[#E2E8F0] dark:border-gray-700'}`}
                />
                <input
                  value={row.name}
                  onChange={(e) => update(row.id, { name: e.target.value })}
                  placeholder="Name (optional)"
                  className={`${rowInputCls} flex-1 border-[#E2E8F0] dark:border-gray-700`}
                />
                <button
                  type="button"
                  onClick={() => remove(row.id)}
                  aria-label="Remove this person"
                  title="Remove"
                  className="shrink-0 w-10 rounded-lg border border-[#E2E8F0] dark:border-gray-700 text-[#94A3B8] hover:text-red-600 hover:border-red-200 flex items-center justify-center transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {issue && <p className="text-[12px] text-red-600 dark:text-red-400 mt-1">{issue}</p>}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={rows.length >= MAX_RECIPIENTS}
        className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline"
      >
        <Plus className="w-3.5 h-3.5" /> Add another person
      </button>

      <p className="text-[12px] text-[#94A3B8] mt-1.5 leading-relaxed">
        Each person gets their own copy, so nobody sees the other addresses.{' '}
        {custom
          ? <>Names fill <code>{'{{first_name}}'}</code> and <code>{'{{name}}'}</code>.</>
          : 'The name is used for the greeting: “Hi Jane,”.'}{' '}
        Tip: paste a list of addresses, or two columns copied from Excel, into an email box. Up to {MAX_RECIPIENTS} people.
      </p>
    </div>
  );
};

const ComposePanel = ({ isOpen, onClose, templates, sender, onSent }) => {
  const [templateKey, setTemplateKey] = useState('introduction');
  const [rows, setRows] = useState(() => [newRow()]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  // After a send that reached only some people: what happened, in a sentence.
  const [notice, setNotice] = useState('');

  const template = templates.find((t) => t.key === templateKey);
  const custom = templateKey === 'custom';
  const awaitingHtml = custom && !body.trim();

  const filledRows = rows.filter((row) => row.email.trim());
  // The preview shows the email as the first listed person will see it.
  const previewFor = filledRows.find((row) => isEmail(row.email)) || { email: '', name: '' };

  // A fresh form each time it opens.
  useEffect(() => {
    if (!isOpen) return;
    const first = templates.find((t) => t.key !== 'custom') || templates[0];
    setTemplateKey(first?.key || 'custom');
    setSubject(first?.subject || '');
    setRows([newRow()]);
    setBody('');
    setFileName('');
    setNotice('');
  }, [isOpen, templates]);

  const chooseTemplate = (t) => {
    setTemplateKey(t.key);
    setSubject(t.subject || '');
  };

  // Custom message: read the chosen .html file straight into the editor. It
  // never goes to the server as a file — only its text, on send.
  const pickHtmlFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.html?$/i.test(file.name) && file.type !== 'text/html') {
      toast.error('Choose an .html file');
      return;
    }
    if (file.size > MAX_HTML_BYTES) {
      toast.error('That file is larger than 2 MB');
      return;
    }
    const html = await file.text();
    setBody(html);
    setFileName(file.name);
    // The file's own <title> is a sensible subject when none is typed yet.
    const title = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1];
    if (!subject.trim() && title && title.trim()) {
      setSubject(new DOMParser().parseFromString(title, 'text/html').documentElement.textContent.trim());
    }
  };

  // Live preview of the real email, rendered by the API.
  useEffect(() => {
    if (!isOpen || !templateKey) return undefined;
    if (awaitingHtml) {
      setPreviewHtml('');
      return undefined;
    }
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await previewOutreachEmail({ templateKey, toEmail: previewFor.email, toName: previewFor.name, subject, body });
        setPreviewHtml(res.data?.html || '');
      } catch (e) {
        // The preview is a convenience; sending validates for real.
      } finally {
        setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [isOpen, templateKey, previewFor.email, previewFor.name, subject, body, awaitingHtml]);

  const badRows = filledRows.filter((row) => !isEmail(row.email)
    || filledRows.filter((other) => emailKey(other) === emailKey(row)).length > 1);

  const problem = !filledRows.length
    ? 'Add at least one email address.'
    : badRows.length
      ? 'Fix the email addresses marked in red.'
      : filledRows.length > MAX_RECIPIENTS
        ? `You can send to at most ${MAX_RECIPIENTS} people at once.`
        : custom && !subject.trim()
          ? 'Enter a subject.'
          : awaitingHtml
            ? 'Upload or paste your HTML email.'
            : '';

  const noUnsubscribe = custom && !awaitingHtml && !/\{\{\s*unsubscribe_url\s*\}\}/i.test(body);
  const clippedByGmail = custom && body.length > GMAIL_CLIP_BYTES;

  /**
   * Everybody sent to → close. Some not sent → stay open with just those
   * people listed, each with the reason, so they can be fixed and sent again.
   */
  const handleResult = ({ sent = [], skipped = [], failed = [] }) => {
    const notSent = [...skipped, ...failed];
    if (sent.length) {
      toast.success(sent.length === 1 ? `Email sent to ${sent[0].email}` : `Email sent to ${sent.length} people`);
    }
    sent.filter((s) => s.warning).forEach((s) => toast.error(`${s.email}: ${s.warning}`));

    if (!notSent.length) {
      onSent({ threadId: sent[0]?.threadId, count: sent.length, close: true });
      return;
    }
    const reasons = new Map(notSent.map((n) => [n.email.toLowerCase(), n.reason]));
    setRows((prev) => {
      const left = prev.filter((row) => reasons.has(emailKey(row)))
        .map((row) => ({ ...row, error: reasons.get(emailKey(row)) }));
      return left.length ? left : [newRow()];
    });
    setNotice(sent.length
      ? `Sent to ${sent.length} ${sent.length === 1 ? 'person' : 'people'}. ${notSent.length} not sent — the reasons are shown below.`
      : `Nothing was sent — the reasons are shown below.`);
    if (sent.length) onSent({ count: sent.length, close: false });
  };

  const send = async () => {
    if (problem) {
      toast.error(problem);
      return;
    }
    if (filledRows.length > 1
      && !window.confirm(`Send this email to ${filledRows.length} people? Each person gets their own copy.`)) {
      return;
    }
    setSending(true);
    setNotice('');
    try {
      const res = await sendOutreachEmail({
        templateKey,
        recipients: filledRows.map((row) => ({ email: row.email.trim(), name: row.name.trim() })),
        subject: subject.trim(),
        body,
      });
      handleResult(res.data || {});
    } catch (e) {
      // 422 means nobody could be sent to; the reasons are still in `data`.
      if (e.response?.data?.data?.sent) handleResult(e.response.data.data);
      else toast.error(e.response?.data?.message || 'Could not send the email');
    } finally {
      setSending(false);
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title="New email"
      subtitle={sender ? `From ${sender.name} <${sender.email}>` : undefined}
      width="sm:max-w-[1100px]"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-[#64748B] dark:text-gray-400 hidden sm:block">
            {custom ? 'Sent exactly as your HTML — no ServPatch header, signature or footer.' : 'Includes an unsubscribe link, as UK marketing rules require.'}
          </span>
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} disabled={sending} className={ghostBtn}>Cancel</button>
            <button type="button" onClick={send} disabled={sending} className={primaryBtn}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Sending…' : filledRows.length > 1 ? `Send to ${filledRows.length} people` : 'Send email'}
            </button>
          </div>
        </div>
      )}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-6">
        <div className="space-y-5">
          {notice && (
            <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 text-[13px] text-red-700 dark:text-red-300">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{notice}</span>
            </div>
          )}

          <RecipientsEditor rows={rows} setRows={setRows} custom={custom} />

          <div>
            <label className="block text-[12.5px] font-bold text-[#475569] dark:text-gray-300 mb-1.5">Email</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {templates.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => chooseTemplate(t)}
                  className={`text-left p-3.5 rounded-xl border transition ${templateKey === t.key
                    ? 'border-blue-500 ring-2 ring-blue-500/15 bg-blue-50/50 dark:bg-blue-500/10'
                    : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#262b33] hover:border-blue-300'}`}
                >
                  <div className="flex items-center gap-2 text-[13.5px] font-bold text-[#1E293B] dark:text-gray-100">
                    {t.key === 'custom' ? <FileCode className="w-4 h-4 text-blue-600" /> : <Mail className="w-4 h-4 text-blue-600" />}
                    {t.label}
                  </div>
                  <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-snug">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[12.5px] font-bold text-[#475569] dark:text-gray-300 mb-1.5">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={template?.subject || 'Subject'} className={inputCls} />
          </div>

          {custom && (
            <div>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="text-[12.5px] font-bold text-[#475569] dark:text-gray-300">Your HTML email</span>
                <label className={`${ghostBtn} cursor-pointer`}>
                  <Upload className="w-3.5 h-3.5" /> Upload .html file
                  <input type="file" accept=".html,.htm,text/html" className="hidden" onChange={pickHtmlFile} />
                </label>
              </div>
              {fileName && (
                <p className="text-[12px] text-[#64748B] dark:text-gray-400 mb-1.5">
                  Loaded <span className="font-semibold">{fileName}</span>. You can still edit it below.
                </p>
              )}
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                spellCheck={false}
                placeholder={'Upload an .html file, or paste your HTML here.\n\n<p>Hi {{first_name}},</p>\n…\n<a href="{{unsubscribe_url}}">Unsubscribe</a>'}
                className="w-full px-3.5 py-2.5 bg-white dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 focus:border-[#2563EB] font-mono text-[12.5px] leading-relaxed text-[#1E293B] dark:text-gray-200 resize-y"
              />
              <p className="text-[12px] text-[#94A3B8] mt-1">
                Sent exactly as it is. You can use <code>{'{{first_name}}'}</code>, <code>{'{{name}}'}</code>, <code>{'{{email}}'}</code> and <code>{'{{unsubscribe_url}}'}</code>; each is filled in for the person you send to.
              </p>
              {noUnsubscribe && (
                <Warning>
                  No unsubscribe link. UK rules require one on marketing emails. Add one to your HTML, e.g. <code>{'<a href="{{unsubscribe_url}}">Unsubscribe</a>'}</code>.
                </Warning>
              )}
              {clippedByGmail && (
                <Warning>This email is over 100 KB. Gmail will cut it off with “[Message clipped]”.</Warning>
              )}
            </div>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12.5px] font-bold text-[#475569] dark:text-gray-300">
              Preview
              {filledRows.length > 1 && previewFor.email && (
                <span className="font-normal text-[#94A3B8]"> · as {previewFor.name || previewFor.email} will see it</span>
              )}
            </span>
            {previewing && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#94A3B8]" />}
          </div>
          {previewHtml && !awaitingHtml && <EmailFrame html={previewHtml} height="calc(100vh - 240px)" />}
          {!previewHtml && !awaitingHtml && (
            <div className="h-[300px] rounded-lg border border-dashed border-[#E2E8F0] dark:border-gray-700 flex items-center justify-center text-[13px] text-[#94A3B8]">Loading preview…</div>
          )}
          {awaitingHtml && (
            <div className="h-[300px] rounded-lg border border-dashed border-[#E2E8F0] dark:border-gray-700 flex flex-col items-center justify-center gap-2 text-[13px] text-[#94A3B8] px-6 text-center">
              <FileCode className="w-7 h-7" strokeWidth={1.5} />
              Upload or paste your HTML to see the email here.
            </div>
          )}
        </div>
      </div>
    </SlideOver>
  );
};

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

const Mailbox = () => {
  const [tab, setTab] = useState('open');
  const [search, setSearch] = useState('');
  const [threads, setThreads] = useState([]);
  const [stats, setStats] = useState({ open: 0, archived: 0, spam: 0, unread: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null); // { thread, messages, suppression }
  const [loadingThread, setLoadingThread] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [sender, setSender] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);

  // Unsent replies, per conversation, so switching away does not lose one.
  const [drafts, setDrafts] = useState({});
  const [sendingReply, setSendingReply] = useState(false);
  const [replyPreview, setReplyPreview] = useState(null);
  const [busyAction, setBusyAction] = useState('');

  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const templateLabels = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.key, t.key === 'custom' ? 'Custom message' : t.label])),
    [templates],
  );

  useEffect(() => {
    fetchOutreachTemplates()
      .then((res) => {
        setTemplates(res.data || []);
        setSender(res.sender || null);
      })
      .catch(() => toast.error('Could not load the email templates'));
  }, []);

  const loadThread = useCallback(async (id, { quiet = false } = {}) => {
    if (!quiet) setLoadingThread(true);
    try {
      const res = await fetchOutreachThread(id);
      setActive(res.data);
    } catch (e) {
      if (!quiet) toast.error(e.response?.data?.message || 'Could not open the conversation');
    } finally {
      if (!quiet) setLoadingThread(false);
    }
  }, []);

  const loadList = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoadingList(true);
    try {
      const res = await fetchOutreachThreads({ status: tab, search: search.trim() || undefined, limit: 100 });
      const rows = res.data || [];
      setThreads(rows);
      setStats(res.stats || {});
      setListError('');
      // New mail in the conversation being read: refresh it in place.
      const current = activeRef.current?.thread;
      const fresh = current && rows.find((t) => t.id === current.id);
      if (fresh && fresh.last_message_at !== current.last_message_at) loadThread(current.id, { quiet: true });
    } catch (e) {
      if (!quiet) setListError(e.response?.data?.message || 'Could not load the mailbox');
    } finally {
      if (!quiet) setLoadingList(false);
    }
  }, [tab, search, loadThread]);

  // Load at once on a tab change (and on mount); on typing, after a pause.
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const searchReady = useRef(false);
  useEffect(() => {
    if (!searchReady.current) {
      searchReady.current = true;
      return undefined;
    }
    const timer = setTimeout(() => loadList(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const timer = setInterval(() => loadList({ quiet: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [loadList]);

  useEffect(() => {
    if (active?.messages?.length) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [active?.thread?.id, active?.messages?.length]);

  const openThread = (thread) => {
    setActiveId(thread.id);
    setActive(null);
    loadThread(thread.id);
    if (thread.unread_count > 0) {
      setThreads((prev) => prev.map((t) => (t.id === thread.id ? { ...t, unread_count: 0 } : t)));
      if (thread.status === 'open') setStats((s) => ({ ...s, unread: Math.max(0, (s.unread || 0) - 1) }));
    }
  };

  const closeThread = () => {
    setActiveId(null);
    setActive(null);
  };

  const changeStatus = async (status, message) => {
    if (!active) return;
    setBusyAction(status);
    try {
      await updateOutreachThread(active.thread.id, { status });
      toast.success(message);
      closeThread();
      loadList({ quiet: true });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update the conversation');
    } finally {
      setBusyAction('');
    }
  };

  const markUnread = async () => {
    if (!active) return;
    setBusyAction('unread');
    try {
      await updateOutreachThread(active.thread.id, { unread: true });
      closeThread();
      loadList({ quiet: true });
    } catch (e) {
      toast.error('Could not mark it unread');
    } finally {
      setBusyAction('');
    }
  };

  const allowAgain = async () => {
    if (!active) return;
    setBusyAction('allow');
    try {
      await allowOutreachEmailsAgain(active.thread.contact_email);
      toast.success('New emails to this address are allowed again');
      loadThread(active.thread.id, { quiet: true });
    } catch (e) {
      toast.error('Could not change that');
    } finally {
      setBusyAction('');
    }
  };

  const draft = (activeId && drafts[activeId]) || '';
  const setDraft = (value) => setDrafts((d) => ({ ...d, [activeId]: value }));

  const previewReply = async () => {
    if (!active || !draft.trim()) return;
    try {
      const res = await previewOutreachEmail({ threadId: active.thread.id, toEmail: active.thread.contact_email, body: draft });
      setReplyPreview(res.data?.html || '');
    } catch (e) {
      toast.error('Could not build the preview');
    }
  };

  const sendReply = async () => {
    if (!active || !draft.trim()) return;
    setSendingReply(true);
    try {
      await replyToOutreachThread(active.thread.id, draft);
      toast.success('Reply sent');
      setDrafts((d) => ({ ...d, [active.thread.id]: '' }));
      setReplyPreview(null);
      await loadThread(active.thread.id, { quiet: true });
      loadList({ quiet: true });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not send the reply');
    } finally {
      setSendingReply(false);
    }
  };

  // A send to one person opens that conversation; a send to several just
  // refreshes the list (each person has their own conversation). `close` is
  // false when some people were not sent to and the composer stays open.
  const onSent = ({ threadId, count = 0, close = true }) => {
    if (close) setComposeOpen(false);
    if (tab !== 'open') setTab('open');
    else loadList({ quiet: true });
    if (close && count === 1 && threadId) {
      setActiveId(threadId);
      loadThread(threadId);
    }
  };

  const thread = active?.thread;
  const suppression = active?.suppression;

  return (
    <div className="h-full flex flex-col gap-4 font-sans">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-[26px] font-bold text-[#1E293B] dark:text-gray-200 tracking-tight">Mailbox</h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1">
            Everything sent to and from <span className="font-semibold text-[#334155] dark:text-gray-300">{sender?.email || 'sameer@servpatch.co.uk'}</span>.
          </p>
        </div>
        <button type="button" onClick={() => setComposeOpen(true)} disabled={!templates.length} className={primaryBtn}>
          <PenSquare className="w-4 h-4" /> New email
        </button>
      </div>

      <div className="flex-1 min-h-0 flex gap-5">
        {/* Left: conversations */}
        <aside
          className={`w-full md:w-[360px] shrink-0 bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 flex-col overflow-hidden ${activeId ? 'hidden md:flex' : 'flex'}`}
        >
          <div className="px-4 pt-4 pb-3 border-b border-[#E2E8F0] dark:border-gray-800 space-y-3">
            <div className="flex gap-1 p-1 bg-[#F1F5F9] dark:bg-[#1f242b] rounded-xl">
              {TABS.map(({ key, label, icon: Icon }) => {
                const count = key === 'open' ? stats.unread : stats[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setTab(key); closeThread(); }}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[12.5px] font-bold transition ${tab === key
                      ? 'bg-white dark:bg-[#31373f] text-[#1E293B] dark:text-gray-100 shadow-sm'
                      : 'text-[#64748B] dark:text-gray-400 hover:text-[#1E293B]'}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] flex items-center justify-center ${key === 'open' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or subject"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 bg-[#F8FAFC] dark:bg-[#31373f] text-[14px] text-[#1E293B] dark:text-gray-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingList && (
              <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
            )}
            {!loadingList && listError && (
              <div className="px-6 py-10 text-center text-[13px] font-semibold text-red-600">{listError}</div>
            )}
            {!loadingList && !listError && threads.length === 0 && (
              <div className="px-6 py-14 text-center">
                <Inbox className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" strokeWidth={1.5} />
                <p className="text-[14px] font-semibold text-gray-600 dark:text-gray-300">
                  {search ? 'Nothing matches that search' : tab === 'open' ? 'No conversations yet' : tab === 'archived' ? 'Nothing archived' : 'No spam'}
                </p>
                {tab === 'open' && !search && (
                  <p className="text-[12.5px] text-gray-400 mt-1">Send a new email, or wait for one to arrive.</p>
                )}
              </div>
            )}
            {!loadingList && !listError && threads.map((t) => {
              const unread = t.unread_count > 0;
              const selected = t.id === activeId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openThread(t)}
                  className={`w-full text-left px-4 py-3.5 border-b border-[#F1F5F9] dark:border-gray-800/60 transition ${selected ? 'bg-blue-50 dark:bg-[#31373f]' : 'hover:bg-[#F8FAFC] dark:hover:bg-[#2c323a]'}`}
                >
                  <div className="flex items-center gap-2">
                    {unread && <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />}
                    <span className={`flex-1 min-w-0 truncate text-[14px] ${unread ? 'font-bold text-[#0F172A] dark:text-white' : 'font-semibold text-[#334155] dark:text-gray-200'}`}>
                      {t.contact_name || t.contact_email}
                    </span>
                    <span className="text-[11px] text-[#94A3B8] shrink-0">{listStamp(t.last_message_at)}</span>
                  </div>
                  <div className={`text-[13px] truncate mt-0.5 ${unread ? 'font-semibold text-[#1E293B] dark:text-gray-100' : 'text-[#475569] dark:text-gray-300'}`}>
                    {t.subject || '(no subject)'}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="flex-1 min-w-0 truncate text-[12.5px] text-[#94A3B8]">
                      {t.last_direction === 'out' ? 'You: ' : ''}{t.last_snippet || ''}
                    </span>
                    {t.last_direction === 'out' && t.last_delivery_status && t.last_delivery_status !== 'sent' && (
                      <DeliveryPill status={t.last_delivery_status} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right: the conversation */}
        <section
          className={`flex-1 min-w-0 bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 flex-col overflow-hidden ${activeId ? 'flex' : 'hidden md:flex'}`}
        >
          {!activeId && (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center mb-4">
                <MailOpen className="w-8 h-8 text-blue-500" strokeWidth={1.5} />
              </div>
              <p className="text-[16px] font-bold text-gray-700 dark:text-gray-200">Pick a conversation</p>
              <p className="text-[13px] text-gray-400 mt-1.5 max-w-[320px]">
                Replies and new emails to {sender?.email || 'sameer@servpatch.co.uk'} appear on the left.
              </p>
            </div>
          )}

          {activeId && (loadingThread || !thread) && (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          )}

          {activeId && !loadingThread && thread && (
            <>
              <div className="px-5 py-4 border-b border-[#E2E8F0] dark:border-gray-800 flex items-start gap-3">
                <button
                  type="button"
                  onClick={closeThread}
                  className="md:hidden w-9 h-9 -ml-1 rounded-full hover:bg-gray-100 dark:hover:bg-[#31373f] flex items-center justify-center shrink-0"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-500" />
                </button>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[17px] font-bold text-[#1E293B] dark:text-gray-100 leading-snug break-words">{thread.subject || '(no subject)'}</h2>
                  <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5 truncate">
                    {thread.contact_name ? `${thread.contact_name} · ` : ''}{thread.contact_email}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2 shrink-0">
                  {thread.status === 'open' && (
                    <>
                      <button type="button" onClick={markUnread} disabled={!!busyAction} className={ghostBtn} title="Mark unread">
                        <Mail className="w-3.5 h-3.5" /><span className="hidden lg:inline">Unread</span>
                      </button>
                      <button type="button" onClick={() => changeStatus('archived', 'Archived')} disabled={!!busyAction} className={ghostBtn}>
                        <Archive className="w-3.5 h-3.5" /><span className="hidden lg:inline">Archive</span>
                      </button>
                      <button type="button" onClick={() => changeStatus('spam', 'Moved to spam')} disabled={!!busyAction} className={ghostBtn}>
                        <ShieldAlert className="w-3.5 h-3.5" /><span className="hidden lg:inline">Spam</span>
                      </button>
                    </>
                  )}
                  {thread.status === 'archived' && (
                    <button type="button" onClick={() => changeStatus('open', 'Moved to inbox')} disabled={!!busyAction} className={ghostBtn}>
                      <Inbox className="w-3.5 h-3.5" /> Move to inbox
                    </button>
                  )}
                  {thread.status === 'spam' && (
                    <button type="button" onClick={() => changeStatus('open', 'Moved to inbox')} disabled={!!busyAction} className={ghostBtn}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Not spam
                    </button>
                  )}
                </div>
              </div>

              {suppression && (
                <div className="px-5 py-2.5 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/30 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px] text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span className="flex-1 min-w-[200px]">
                    Opted out of marketing emails — {OPT_OUT_REASONS[suppression.reason] || suppression.reason}, {listStamp(suppression.created_at)}. You can still reply here; new emails are blocked.
                  </span>
                  <button type="button" onClick={allowAgain} disabled={busyAction === 'allow'} className="font-bold underline hover:no-underline">
                    Allow emails again
                  </button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-4 md:px-5 py-5 space-y-4 bg-[#F8FAFC] dark:bg-[#1f242b]">
                {(active.messages || []).map((m) => (
                  <MessageCard key={m.id} message={m} templateLabels={templateLabels} />
                ))}
                <div ref={bottomRef} />
              </div>

              {thread.status !== 'spam' && (
                <div className="border-t border-[#E2E8F0] dark:border-gray-800 p-4 bg-white dark:bg-[#262b33]">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={4}
                    placeholder={`Reply to ${thread.contact_name || thread.contact_email}…`}
                    className={`${inputCls} resize-y leading-relaxed`}
                  />
                  <div className="flex items-center justify-between gap-3 mt-2.5">
                    <span className="flex-1 min-w-0 text-[12px] text-[#94A3B8] hidden sm:block">
                      Sent as {sender?.name || 'Sameer'} with the ServPatch header, signature and footer. The email you’re answering is quoted below it.
                    </span>
                    <div className="flex gap-2 ml-auto">
                      <button type="button" onClick={previewReply} disabled={!draft.trim() || sendingReply} className={ghostBtn}>
                        <Eye className="w-3.5 h-3.5" /> Preview
                      </button>
                      <button type="button" onClick={sendReply} disabled={!draft.trim() || sendingReply} className={primaryBtn}>
                        {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Reply className="w-4 h-4" />}
                        {sendingReply ? 'Sending…' : 'Send reply'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <ComposePanel
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        templates={templates}
        sender={sender}
        onSent={onSent}
      />

      <Modal
        isOpen={replyPreview !== null}
        onClose={() => setReplyPreview(null)}
        title="Reply preview"
        maxWidth="max-w-3xl"
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setReplyPreview(null)} className={ghostBtn}>Keep editing</button>
            <button type="button" onClick={sendReply} disabled={sendingReply} className={primaryBtn}>
              {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send reply
            </button>
          </div>
        )}
      >
        {replyPreview && <EmailFrame html={replyPreview} height="65vh" />}
      </Modal>
    </div>
  );
};

export default Mailbox;
